import { collection, getDocs } from "firebase/firestore";
import { create } from "zustand";
import { firestore } from "../firebase/firebase";

const useUsers = create((set, get) => ({
  users: [],
  analytics: {
    totalUsers: 0,
    totalCredits: 0,
    avgCreditsPerUser: 0,
    lifetimeUsers: 0,
    referredUsers: 0,
    successfulReferrers: 0,
    totalSearches: 0,
    avgSearchesPerUser: 0,
    avgReferralsPerUser: 0,
    lastActiveUsers: 0,
    domainStats: {},
    referralStats: {},
  },
  loading: true,

  // Separate function just to fetch users
  fetchUsers: async () => {
    try {
      const usersRef = collection(firestore, "users");
      const usersSnap = await getDocs(usersRef);
      const usersData = usersSnap.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      set({ users: usersData, loading: false });
    } catch (error) {
      console.error("Error fetching users:", error);
      set({ loading: false });
    }
  },

  // Separate function to calculate analytics
  calculateAnalytics: () => {
    const usersData = get().users;

    if (!usersData?.length) return;

    // Calculate referral metrics
    const referredUsers = usersData.filter((user) => user.referrerId).length;
    const uniqueReferrers = new Set(
      usersData.filter((user) => user.referrerId).map((user) => user.referrerId)
    ).size;

    // Calculate total searches
    const totalSearches = usersData.reduce(
      (sum, user) => sum + (user.searchCount || 0),
      0
    );

    // Calculate domain statistics
    const domainStats = usersData.reduce((acc, user) => {
      if (user.email) {
        const domain = user.email.split("@")[1];
        acc[domain] = (acc[domain] || 0) + 1;
      }
      return acc;
    }, {});

    // Calculate referral statistics
    const referralStats = usersData.reduce((acc, user) => {
      if (user.referralCode) {
        const referredCount = usersData.filter(
          (u) => u.referrerId === user.id
        ).length;
        if (referredCount > 0) {
          acc[user.referralCode] = {
            count: referredCount,
            user: user.email,
            totalCredits: usersData
              .filter((u) => u.referrerId === user.id)
              .reduce((sum, u) => sum + (u.credits || 0), 0),
            activeUsers: usersData.filter(
              (u) => u.referrerId === user.id && u.lastReferralRewardDate
            ).length,
          };
        }
      }
      return acc;
    }, {});

    const totalAdmins = usersData.filter((user) => user.isAdmin).length;

    // Calculate total and average credits
    const totalCredits = usersData.reduce(
      (sum, user) => sum + (user.credits || 0),
      0
    );

    // Update analytics state
    set({
      analytics: {
        totalUsers: usersData.length,
        totalCredits,
        avgCreditsPerUser: Math.round(totalCredits / usersData.length || 0),
        lifetimeUsers: usersData.filter(
          (user) => user.subscriptionPeriod === "lifetime"
        ).length,
        referredUsers,
        successfulReferrers: uniqueReferrers,
        totalSearches,
        avgSearchesPerUser: Math.round(totalSearches / usersData.length || 0),
        avgReferralsPerUser: (referredUsers / usersData.length).toFixed(2),
        lastActiveUsers: usersData.filter((user) => user.lastReferralRewardDate)
          .length,
        domainStats,
        referralStats,
        totalAdmins,
      },
    });
  },
}));

export default useUsers;
